import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "../api";
import { useLang } from "../i18n";
import { AREA_META } from "../constants/areaMeta";
import { RING_COLORS } from "../constants/brand";
import { ArrowUpRight, BookOpen, Check } from "lucide-react";

function Ring({ pct, color, size = 96, stroke = 9, children }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const off = circ - (Math.min(pct, 100) / 100) * circ;
  return (
    <svg width={size} height={size} className="shrink-0" style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeOpacity={0.15} strokeWidth={stroke} />
      <motion.circle
        cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={circ}
        initial={{ strokeDashoffset: circ }}
        animate={{ strokeDashoffset: off }}
        transition={{ duration: 1, ease: "easeOut" }}
      />
      {children}
    </svg>
  );
}

export function ActivityRings() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [rings, setRings] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get(`/rings?lang=${lang}`).then((r) => setRings(r.data.rings || [])).catch(() => {});
  }, [lang]);

  if (!rings.length) return null;
  const sel = rings.find((r) => r.pillar === selected);

  return (
    <div className="rounded-[2rem] p-8 bg-white border border-[#3F6E85]/10 shadow-[0_8px_32px_rgba(63,110,133,0.06)] mb-8" data-testid="activity-rings">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-3xl tracking-tight text-[#1A1D20]">{t("rings_title")}</h2>
        <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#3F6E85]">{t("rings_today")}</span>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-4">
        {rings.map((rg) => {
          const meta = AREA_META[rg.pillar] || {};
          const Icon = meta.icon;
          const color = RING_COLORS[rg.pillar] || "#3F6E85";
          const active = selected === rg.pillar;
          return (
            <button
              key={rg.pillar}
              data-testid={`ring-${rg.pillar}`}
              onClick={() => setSelected(active ? null : rg.pillar)}
              className="flex flex-col items-center gap-2 group focus-visible:outline-none"
            >
              <motion.div whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.97 }} className="relative">
                <Ring pct={rg.today_fill} color={color} />
                <span className="absolute inset-0 flex items-center justify-center">
                  {rg.today_done ? (
                    <Check className="w-7 h-7" style={{ color }} strokeWidth={2.5} />
                  ) : (
                    Icon && <Icon className="w-6 h-6" style={{ color }} strokeWidth={1.75} />
                  )}
                </span>
                {active && <span className="absolute -inset-1 rounded-full ring-2" style={{ borderColor: color, boxShadow: `0 0 0 3px ${color}22` }} />}
              </motion.div>
              <span className="text-xs font-medium text-[#1A1D20] text-center leading-tight">{t(rg.pillar)}</span>
            </button>
          );
        })}
      </div>

      <AnimatePresence initial={false}>
        {sel && (
          <motion.div
            key={sel.pillar}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-6 rounded-[1.5rem] p-6 bg-[#F3F1ED]" data-testid={`ring-detail-${sel.pillar}`} style={{ borderLeft: `5px solid ${RING_COLORS[sel.pillar]}` }}>
              <div className="flex items-center justify-between gap-3 mb-1">
                <h3 className="font-serif text-2xl text-[#1A1D20]">{t(sel.pillar)}</h3>
                {sel.is_focus && <span className="text-[10px] uppercase tracking-wider text-[#B5651D] font-bold">focus</span>}
              </div>
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#6C757D] mb-3">{t("monthly_objective")}</p>
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 h-3 rounded-full bg-white overflow-hidden">
                  <motion.div className="h-full rounded-full" style={{ backgroundColor: RING_COLORS[sel.pillar] }}
                    initial={{ width: 0 }} animate={{ width: `${sel.monthly_pct}%` }} transition={{ duration: 0.8 }} />
                </div>
                <span className="text-sm font-semibold text-[#1A1D20]" data-testid={`ring-monthly-${sel.pillar}`}>{sel.monthly_label}</span>
              </div>
              <p className="text-sm text-[#1A1D20]/75 leading-relaxed mb-5">{sel.monthly_tip}</p>
              <div className="flex flex-wrap gap-3">
                <button onClick={() => navigate(`/learn/${sel.pillar}`)} data-testid={`ring-learn-${sel.pillar}`}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.03]" style={{ backgroundColor: RING_COLORS[sel.pillar] }}>
                  <BookOpen className="w-4 h-4" /> {t("how_to_do")}
                </button>
                <button onClick={() => navigate(`/track?pillar=${sel.pillar}`)} data-testid={`ring-track-${sel.pillar}`}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold bg-white border border-[#3F6E85]/20 text-[#3F6E85] transition-transform hover:scale-[1.03]">
                  {t("register_today")} <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
